<!--
  MembersChoiceProductCard.vue — one Members Choice voting product: a
  performer rail on the left (avatar, name, then the product title/description
  underneath, since there's room to spare) sits beside a row of 2-5 tall
  vertical-video options (title/tally + vote controls under each). Expiry/stats
  trail in the top-right corner. Winner ribbon once voting has closed.

  Vote state (localVotes/voteBurst/customAmounts) lives in Index.vue so the
  batching + rollback logic has a single owner; this card renders and emits.
-->
<script setup>
import { reactive, ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { Play } from 'lucide-vue-next'
import StarIcon from './StarIcon.vue'
import StarMarkSVG from './StarMarkSVG.vue'
import ShareStarProductButton from './ShareStarProductButton.vue'
import CelebrationBurst from './CelebrationBurst.vue'
import { useImpressionTracking } from '@/composables/useImpressionTracking'

const props = defineProps({
    product:        { type: Object, required: true },
    localVotes:     { type: Object, default: () => ({}) },
    voteBurst:      { type: Object, default: () => ({}) },
    customAmounts:  { type: Object, default: () => ({}) },
    votingOptionId: { type: [Number, null], default: null },
    isCopied:       { type: Boolean, default: false },
    balance:        { type: Number, default: 0 },
})

const emit = defineEmits(['vote', 'free-vote', 'share', 'buy-stars', 'option-viewed', 'cta-clicked'])

function trackCtaClick(cta) {
    emit('cta-clicked', props.product.id, cta)
}

const { observeElement } = useImpressionTracking()

// Free-amount options start collapsed to a single "Vote" button; clicking it
// reveals the preset/amount picker. Only one option expands at a time (per
// card) — expanding a second one implicitly collapses whichever was open.
const expandedOptionId = ref(null)

function toggleExpand(optionId) {
    expandedOptionId.value = expandedOptionId.value === optionId ? null : optionId
}

// Auto-collapse back to the Vote button once a submitted vote finishes
// (votingOptionId goes from this option's id back to null) rather than
// leaving the picker open after a successful/failed submit.
watch(() => props.votingOptionId, (current, previous) => {
    if (previous && !current && expandedOptionId.value === previous) {
        expandedOptionId.value = null
    }
})

// One shared celebration burst for the whole card (see the template comment
// by its usage) — bumps whenever any of this product's options' voteBurst
// counters change, reusing Index.vue's existing validity-gated increments
// (queueVote()/sendVote()) rather than re-deriving "was this a real vote" here.
const burstTrigger = ref(0)
watch(
    () => props.product.options.map((o) => props.voteBurst[o.id] ?? 0).join(','),
    (val, oldVal) => { if (val !== oldVal) burstTrigger.value++ }
)

function displayVotes(option) {
    return (option.votes ?? 0) + (props.localVotes[option.id] ?? 0)
}

// Card-level totals for the rail's "X stars cast · Y voters" line and each
// option's "% of votes" bar — derived client-side so they stay in sync with
// optimistic localVotes rather than the cached total_votes from the backend.
function totalStarsCast() {
    return props.product.options.reduce((sum, o) => sum + displayVotes(o), 0)
}

function totalVoters() {
    // Backend-computed distinct count across all of this product's options —
    // summing each option's own `voters` here would double-count anyone who
    // voted on more than one option.
    return props.product.total_voters ?? 0
}

function votePct(option) {
    const total = totalStarsCast()
    return total > 0 ? Math.round((displayVotes(option) / total) * 100) : 0
}

// Videos ship with no native controls (play/pause only); track state per option so
// the overlay play icon hides while playing, per VideoPlayer.vue's convention.
const videoPlaying = reactive({})

// iOS Safari refuses to actually buffer/render a video's first frame just
// from preload="auto" — it's known to ignore that hint without a real user
// gesture, so the element stays blank/black until tapped.
//
// Two things were each tried alone and each failed a different way:
// - A #t=0.1 media-fragment baked into src DOES force Safari to fetch/paint
//   that frame (confirmed working on staging), but Safari can then treat the
//   in-URL fragment as a playback RANGE restriction — tapping play fired
//   @play (overlay hid) but playback never advanced past that instant.
// - Seeking currentTime via JS on loadedmetadata avoids that restriction
//   (confirmed playback works), but doesn't reliably force a frame to
//   render — loadedmetadata only guarantees duration/dimensions are known,
//   not that decodable frame data has actually been fetched, and iOS won't
//   fetch that without a gesture regardless of preload.
//
// So: keep the fragment for the poster (it's the only thing that reliably
// forces the frame), and strip it back to the plain URL right when the user
// taps play, before calling play() — so the fragment never survives into
// actual playback.
function posterFrameSrc(url) {
    return url + '#t=0.1'
}

function toggleVideoPlay(event) {
    const video = event.currentTarget
    if (video.paused) {
        // Only one option video plays at a time — across every Members Choice
        // card on the page, not just this product's own options (the "see all"
        // modal renders many cards' videos at once).
        document.querySelectorAll('video').forEach((v) => {
            if (v !== video && !v.paused) v.pause()
        })
        if (video.src.includes('#t=')) {
            video.src = video.src.split('#')[0]
            video.load()
        }
        // Unmute on the same tap/click that starts playback — this is a direct
        // user gesture, so it works on touch devices too (unlike hover, which
        // mobile browsers don't fire from a tap).
        video.muted = false
        video.play()
    } else {
        video.pause()
    }
}

// Desktop-only nicety: hovering a paused video primes it unmuted, so a click
// right after immediately plays with sound with no perceptible delay.
function unmuteVideo(event) {
    event.currentTarget.muted = false
}

// Reset to muted once a video stops, so the next play (elsewhere on the page,
// or a future one here) never inherits an unmuted state it didn't ask for.
function remuteVideo(event) {
    event.currentTarget.muted = true
}

function leadingOptionId() {
    let leadId = null
    let best = 0
    for (const o of props.product.options) {
        const v = displayVotes(o)
        if (v > best) {
            best = v
            leadId = o.id
        }
    }
    return leadId
}

const CLOSING_SOON_DAYS = 7

function expiryLabel(daysLeft) {
    if (daysLeft === null || daysLeft === undefined) return null
    if (daysLeft <= 0) return 'Closes today'
    if (daysLeft === 1) return '1 day left'
    if (daysLeft < 30) return `${daysLeft} days left`
    const months = Math.floor(daysLeft / 30)
    return months === 1 ? '1 month left' : `${months} months left`
}

function isClosingSoon(daysLeft) {
    return daysLeft !== null && daysLeft !== undefined && daysLeft <= CLOSING_SOON_DAYS
}

const DAY_MS = 24 * 60 * 60 * 1000

// Ticks once a second so expiryDisplay can count down live once under 24h —
// "Closes today" otherwise held for the entire final day with no urgency cue.
const nowTick = ref(Date.now())
let countdownTimer = null

function pad(n) {
    return String(n).padStart(2, '0')
}

const expiryDisplay = computed(() => {
    if (!props.product.expires_at) return expiryLabel(props.product.days_left)

    const msLeft = new Date(props.product.expires_at).getTime() - nowTick.value
    if (msLeft <= 0) return 'Closes today'
    if (msLeft >= DAY_MS) return expiryLabel(props.product.days_left)

    const totalSeconds = Math.floor(msLeft / 1000)
    const h = Math.floor(totalSeconds / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    const s = totalSeconds % 60
    return (h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`) + ' left'
})

// preload="auto" (needed to force iOS to paint the poster frame, see
// posterFrameSrc) makes the browser eagerly buffer whatever src a <video>
// has — with ALL of a product's options rendered into the DOM regardless of
// scroll position, that meant every option's video loaded at once,
// competing for bandwidth and delaying the ones actually on screen. Only
// give an option a real src (activating loading) once it's actually visible
// within the row; others stay src-less (nothing to load) until they scroll
// into view. rootMargin is intentionally 0 (not a lookahead buffer) — a
// generous margin here doesn't just preload the next option in this row,
// it also preloads an entire ADJACENT PRODUCT's videos the moment that
// product's card so much as peeks into the outer carousel (that row's own
// bounding rect, even barely on-screen, can fall entirely within a large
// margin) — exactly the "everything loads at once" problem this exists to
// fix, just one level up.
const loadedOptionIds = reactive(new Set())
const optionMediaEls = new Map()

function setOptionMediaRef(id, el) {
    if (el) optionMediaEls.set(id, el)
    else optionMediaEls.delete(id)
}

function optionVideoSrc(option) {
    return loadedOptionIds.has(option.id) ? posterFrameSrc(option.media_url) : undefined
}

let lazyLoadObserver = null

// IntersectionObserver's first callback (like ResizeObserver's, see
// observeFirstMedia below) is deferred to the browser's next paint pass —
// it can sit unfired for a while in a backgrounded/automated tab. Read the
// initial state synchronously via getBoundingClientRect so an option that's
// already visible at mount isn't stuck src-less waiting on a callback that
// may not fire for a while; the observer then only needs to catch options
// becoming visible LATER (actual scrolling).
function isInViewport(el) {
    const r = el.getBoundingClientRect()
    return r.right > 0 && r.left < window.innerWidth && r.bottom > 0 && r.top < window.innerHeight
}

function observeOptionsForLazyLoad() {
    if (lazyLoadObserver) lazyLoadObserver.disconnect()
    if (!optionsRowEl.value) return
    optionMediaEls.forEach((el, id) => {
        if (isInViewport(el)) loadedOptionIds.add(id)
    })
    lazyLoadObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                loadedOptionIds.add(Number(entry.target.dataset.optionId))
            }
        })
    }, {
        // root: null (the page viewport), not the scrolling row itself — a
        // nested scroll-root (row inside the outer product track, itself
        // scrolling) is where browsers' ancestor-clipping behavior gets
        // inconsistent; a barely-peeking adjacent product's row was reporting
        // its options as fully intersecting despite sitting far outside the
        // viewport. Plain viewport-relative intersection is unambiguous:
        // actually on screen or not, regardless of scroll nesting depth.
        root: null,
        rootMargin: '0px',
        threshold: 0.01,
    })
    optionMediaEls.forEach((el) => lazyLoadObserver.observe(el))
}

// Impression tracking (analytics, separate concern from the lazy-load
// observer above) — fires once per option, the moment it's actually visible,
// so a Tinybird report can compute options-viewed vs. options-voted-for.
function observeOptionsForImpressions() {
    optionMediaEls.forEach((el, id) => {
        observeElement(el, () => emit('option-viewed', props.product.id, id))
    })
}

// The options row is a native scroll-snap swipe (one tile at a time on
// mobile, three at a time on desktop) with no arrows or dots — instead, a
// soft edge fade hints there's more to scroll to, shown only on whichever
// side actually has more content (never both fades on a single-option
// product, and the leading edge disappears once you've swiped all the way to
// the last option).
const optionsRowEl = ref(null)
const isAtStart = ref(true)
const isAtEnd = ref(false)

function updateScrollEdges(el) {
    isAtStart.value = el.scrollLeft <= 1
    isAtEnd.value = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
}

// A swipe to another option (mobile: exactly one option visible at a time)
// shouldn't leave the previous one still playing offscreen — pause any
// playing video once it scrolls out of the row's visible bounds. rAF
// -throttled since scroll fires far more often than needed for this check.
let optionsScrollRaf = null

function pauseOffscreenVideos(row) {
    const rowRect = row.getBoundingClientRect()
    row.querySelectorAll('video').forEach((video) => {
        if (video.paused) return
        const r = video.getBoundingClientRect()
        const visible = r.right > rowRect.left && r.left < rowRect.right
        if (!visible) video.pause()
    })
}

function onOptionsScroll(event) {
    updateScrollEdges(event.currentTarget)
    if (optionsScrollRaf) cancelAnimationFrame(optionsScrollRaf)
    const row = event.currentTarget
    optionsScrollRaf = requestAnimationFrame(() => pauseOffscreenVideos(row))
}

// Click-and-drag scrolling (desktop/mouse only — touch already scrolls
// natively via the browser). Tracks whether the pointer actually moved past a
// small threshold so a plain click on a Vote button or thumbnail still fires
// normally instead of being swallowed as a drag.
const isDragging = ref(false)
let dragMoved = false
let dragStartX = 0
let dragStartScrollLeft = 0

function onOptionsDragStart(event) {
    const row = optionsRowEl.value
    if (!row) return
    isDragging.value = true
    dragMoved = false
    dragStartX = event.pageX
    dragStartScrollLeft = row.scrollLeft
}

function onOptionsDragMove(event) {
    if (!isDragging.value) return
    const row = optionsRowEl.value
    if (!row) return
    const delta = event.pageX - dragStartX
    if (Math.abs(delta) > 5) dragMoved = true
    row.scrollLeft = dragStartScrollLeft - delta
}

function onOptionsDragEnd() {
    isDragging.value = false
}

// Dragged past the threshold — suppress the click that would otherwise fire
// on whatever the pointer lands on (Vote button, thumbnail link, etc.).
function onOptionsClickCapture(event) {
    if (dragMoved) {
        event.stopPropagation()
        event.preventDefault()
        dragMoved = false
    }
}

// The fade frame's height must match a single tile's rendered height, not an
// aspect-ratio computed off the frame's own width — the frame spans the
// full row (all 3 desktop columns), so deriving height from ITS width via
// aspect-ratio blew the fade up to ~3x the media's actual height and let it
// spill down over the sections below. Measuring the first option's media
// element directly keeps the fade pinned to the actual photo/video height at
// any breakpoint or column count.
const frameHeight = ref(0)
let mediaResizeObserver = null

function observeFirstMedia() {
    if (mediaResizeObserver) mediaResizeObserver.disconnect()
    const media = optionsRowEl.value?.querySelector('.mc-option__media')
    if (!media) return
    // Read synchronously up front — ResizeObserver's own first callback is
    // deferred to the browser's next paint pass, which can sit unfired for a
    // while in a backgrounded/automated tab, leaving the frame at height 0.
    frameHeight.value = media.getBoundingClientRect().height
    mediaResizeObserver = new ResizeObserver(([entry]) => {
        frameHeight.value = entry.contentRect.height
    })
    mediaResizeObserver.observe(media)
}

onMounted(() => {
    nextTick(() => {
        if (optionsRowEl.value) updateScrollEdges(optionsRowEl.value)
        observeFirstMedia()
        observeOptionsForLazyLoad()
        observeOptionsForImpressions()
    })
    countdownTimer = setInterval(() => { nowTick.value = Date.now() }, 1000)
})

onBeforeUnmount(() => {
    if (countdownTimer) clearInterval(countdownTimer)
    if (mediaResizeObserver) mediaResizeObserver.disconnect()
    if (lazyLoadObserver) lazyLoadObserver.disconnect()
})
</script>

<template>
    <article class="mc-product" :data-product-id="product.id">
        <!-- Mobile-only compact header (≤640px, see media query below) — the
             desktop header + performer rail below stay in the DOM but are hidden
             on mobile via CSS rather than duplicating the vote/options markup. -->
        <div class="mc-mobile-head">
            <div class="mc-mobile-head__top">
                <div class="mc-mobile-avatar">
                    <img v-if="product.performer_photo" :src="product.performer_photo" :alt="product.performer_name" class="mc-mobile-avatar__img" />
                </div>
                <div class="mc-mobile-head__name">{{ product.performer_name }}</div>
            </div>

            <div class="mc-mobile-head__meta">
                <span v-if="product.expired" class="mc-closed-chip">Voting closed</span>
                <span v-else-if="expiryDisplay" class="mc-badge mc-badge--time" :class="{ 'mc-badge--urgent': isClosingSoon(product.days_left) }">
                    <StarIcon name="clock" :size="10" :stroke-width="2.5" /> {{ expiryDisplay }}
                </span>
                <ShareStarProductButton :copied="isCopied" @share="emit('share', product.id)" />
            </div>

            <p v-if="product.description" class="mc-muted mc-mobile-head__desc">{{ product.description }}</p>

            <div v-if="product.ctas?.length" class="mc-cta-list mc-mobile-head__ctas">
                <a v-for="cta in product.ctas" :key="cta.url" :href="cta.url" class="mc-cta-link" @click="trackCtaClick(cta)">
                    {{ cta.label }} →
                </a>
            </div>

            <div class="mc-mobile-stats">
                <div class="mc-mobile-stats__row">
                    <div class="mc-mobile-stats__cast">
                        <strong>{{ totalStarsCast().toLocaleString() }}</strong> cast · <strong>{{ totalVoters() }}</strong> voters
                    </div>
                    <div class="mc-mobile-stats__balance">
                        <StarMarkSVG :size="14" /> <strong>{{ balance.toLocaleString() }}</strong> stars
                    </div>
                </div>
                <button type="button" class="mc-balance__cta mc-mobile-stats__cta" @click="emit('buy-stars')">Get more</button>
            </div>
        </div>

        <div class="mc-product__head">
            <div class="mc-product__head-left">
                <span class="mc-product__title">Vote for {{ product.name }}</span>
                <span class="mc-muted">{{ product.options.length }} options</span>
            </div>
            <div class="mc-product__head-right">
                <div class="mc-balance">
                    <StarMarkSVG :size="15" />
                    <span class="mc-balance__num">{{ balance.toLocaleString() }}</span>
                    <span class="mc-balance__lbl">stars</span>
                    <button type="button" class="mc-balance__cta" @click="emit('buy-stars')">Get more</button>
                </div>
                <ShareStarProductButton :copied="isCopied" @share="emit('share', product.id)" />
            </div>
        </div>

        <div class="mc-product__layout">
            <div class="mc-product__performer" v-if="product.performer_name || product.performer_photo">
                <div class="mc-product__photo">
                    <img v-if="product.performer_photo" :src="product.performer_photo" :alt="product.performer_name" class="mc-product__photo-img" />
                </div>

                <div v-if="product.performer_name" class="mc-product__performer-name">{{ product.performer_name }}</div>
                <p v-if="product.description" class="mc-muted mc-product__desc">{{ product.description }}</p>

                <div class="mc-product__rail-foot">
                    <div class="mc-product__badges">
                        <span v-if="product.expired" class="mc-closed-chip">Voting closed</span>
                        <template v-else>
                            <span v-if="expiryDisplay" class="mc-badge mc-badge--time" :class="{ 'mc-badge--urgent': isClosingSoon(product.days_left) }">
                                <StarIcon name="clock" :size="10" :stroke-width="2.5" /> {{ expiryDisplay }}
                            </span>
                            <span class="mc-badge mc-badge--open">Voting open</span>
                        </template>
                    </div>
                    <div class="mc-muted mc-product__stats">{{ totalStarsCast().toLocaleString() }} stars cast · {{ totalVoters() }} voter{{ totalVoters() === 1 ? '' : 's' }}</div>
                </div>

                <div v-if="product.ctas?.length" class="mc-cta-list">
                    <a v-for="cta in product.ctas" :key="cta.url" :href="cta.url" class="mc-cta-link" @click="trackCtaClick(cta)">
                        {{ cta.label }} →
                    </a>
                </div>
            </div>

            <div class="mc-product__content">
                <!-- Edge fade, hinting there's more to swipe/scroll to. A sibling
                     of the scrolling row (not a pseudo-element on it), positioned
                     to match .mc-option__media exactly — a fade living INSIDE the
                     scroll container scrolls away with the content (only ever
                     visible over the first/last option); this stays fixed over
                     the viewport since it isn't part of what scrolls. Height is
                     measured off the first tile's media (frameHeight, via
                     ResizeObserver) rather than derived from the frame's own
                     width via aspect-ratio — the frame spans the FULL row
                     (all 3 desktop columns), so an aspect-ratio there produced a
                     height 3x too tall and spilled the fade down over the
                     sections below. Each fade's border-radius matches the
                     media's rounded corners on its side so it doesn't visually
                     square them off. -->
                <div class="mc-options-frame" :class="{ 'mc-options-frame--at-start': isAtStart, 'mc-options-frame--at-end': isAtEnd }"
                     :style="{ height: frameHeight + 'px' }">
                    <span class="mc-options-fade mc-options-fade--left"></span>
                    <span class="mc-options-fade mc-options-fade--right"></span>
                </div>

                <div class="mc-options-row" ref="optionsRowEl" @scroll="onOptionsScroll"
                     :class="{ 'mc-options-row--scrollable': product.options.length > 3, 'mc-options-row--dragging': isDragging }"
                     @mousedown="onOptionsDragStart" @mousemove="onOptionsDragMove"
                     @mouseup="onOptionsDragEnd" @mouseleave="onOptionsDragEnd"
                     @click.capture="onOptionsClickCapture">
                    <div v-for="o in product.options" :key="o.id" class="mc-option"
                         :class="{ 'mc-option--winner': product.expired && o.id === product.winner_option_id, 'mc-option--leading': !product.expired && o.id === leadingOptionId() }">
                        <div class="mc-option__media" :ref="(el) => setOptionMediaRef(o.id, el)" :data-option-id="o.id">
                            <!-- No separate poster image exists for these options (unlike
                                 Components/Reels/VideoPlayer.vue, which layers a real poster
                                 <img> — no such field exists on star_shop_product_options).
                                 preload="auto" alone didn't fix iOS Safari showing a blank/
                                 black box — it ignores that hint without a user gesture — so
                                 posterFrameSrc()'s #t=0.1 fragment forces Safari to paint that
                                 frame; toggleVideoPlay strips the fragment back off before
                                 actually playing (see comment there). src is withheld (see
                                 optionVideoSrc/loadedOptionIds above) until this option is
                                 actually near the visible row — otherwise every option's
                                 video eagerly buffers at once via preload="auto". -->
                            <video v-if="o.media_type === 'video'"
                                   :src="optionVideoSrc(o)"
                                   class="mc-option__asset"
                                   playsinline webkit-playsinline muted
                                   preload="auto" disablePictureInPicture
                                   @click="toggleVideoPlay"
                                   @mouseenter="unmuteVideo"
                                   @play="videoPlaying[o.id] = true"
                                   @pause="videoPlaying[o.id] = false; remuteVideo($event)"
                                   @ended="videoPlaying[o.id] = false; remuteVideo($event)" />
                            <img v-else :src="o.media_url" :alt="o.title" loading="lazy" class="mc-option__asset" />
                            <span v-if="o.media_type === 'video' && !videoPlaying[o.id]" class="mc-play-overlay">
                                <Play :size="18" fill="currentColor" :stroke-width="0" />
                            </span>
                            <span v-if="product.expired && o.id === product.winner_option_id" class="mc-ribbon">
                                <StarIcon name="trophy" :size="10" :stroke-width="2.5" /> Winner
                            </span>
                            <span v-else-if="!product.expired && o.id === leadingOptionId()" class="mc-leading">Leading</span>
                        </div>
                        <div class="mc-option__body">
                            <div class="mc-option__text">
                                <div class="mc-option__title">{{ o.title }}</div>
                                <p v-if="o.description" class="mc-muted mc-option__desc">{{ o.description }}</p>
                            </div>
                            <div class="mc-option__tally">
                                <span class="mc-option__tally-left">
                                    <StarMarkSVG :size="11" />
                                    <span class="mc-option__count">{{ displayVotes(o).toLocaleString() }}</span> stars
                                    <span class="mc-muted">· {{ o.voters }} voter{{ o.voters === 1 ? '' : 's' }}</span>
                                </span>
                                <span class="mc-option__pct">{{ votePct(o) }}%</span>
                            </div>

                            <div class="mc-bar"><div class="mc-bar__fill" :style="{ width: votePct(o) + '%' }" /></div>

                            <template v-if="!product.expired">
                                <div v-if="product.format === 'single'" class="mc-vote-wrap">
                                    <button class="mc-btn mc-btn--full" @click="emit('vote', product, o)">
                                        <StarMarkSVG :size="11" /> Vote · 1 star
                                    </button>
                                </div>
                                <div v-else class="mc-vote-wrap">
                                    <button v-if="expandedOptionId !== o.id" class="mc-btn mc-btn--full" @click="toggleExpand(o.id)">
                                        <StarMarkSVG :size="11" /> Vote
                                    </button>
                                    <div v-else class="mc-vote-expanded">
                                        <div class="mc-pick-row">
                                            <button v-for="amt in [25, 50, 100]" :key="amt" class="mc-btn mc-btn--preset"
                                                    :disabled="votingOptionId === o.id"
                                                    @click="emit('free-vote', product, o, amt)">
                                                <StarMarkSVG :size="9" /> {{ amt }}
                                            </button>
                                        </div>
                                        <div class="mc-amount-row">
                                            <div class="mc-amount-field">
                                                <StarMarkSVG class="mc-amount-field__icon" :size="11" />
                                                <input v-model.number="customAmounts['opt_' + o.id]" type="number" min="1"
                                                       placeholder="Enter amount…" class="mc-amount-input"
                                                       @keyup.enter="customAmounts['opt_' + o.id] && emit('free-vote', product, o, null)" />
                                            </div>
                                            <button class="mc-btn mc-btn--submit"
                                                    :disabled="votingOptionId === o.id || !customAmounts['opt_' + o.id]"
                                                    @click="emit('free-vote', product, o, null)">→</button>
                                        </div>
                                        <button type="button" class="mc-vote-cancel" @click="toggleExpand(o.id)">Cancel</button>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
                <!-- Sibling of the scrolling .mc-options-row (which has
                     overflow-x: auto), not nested inside it — a per-option
                     burst there had its radiating particles clipped at the
                     option's own edges. One shared burst here, anchored to
                     .mc-product__content instead, celebrates any vote on this
                     product without anything clipping it. -->
                <div class="mc-burst-anchor"><CelebrationBurst :trigger="burstTrigger" /></div>
            </div>
        </div>
    </article>
</template>

<style scoped>
.mc-product {
    position: relative;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(255,255,255,0.13);
    border-radius: 14px;
    padding: 18px;
}
.mc-muted { color: #eaeaea; font-size: 12px; margin: 0; }

/* Compact mobile header — hidden on desktop, swapped in for the header +
   performer rail below at the ≤640px breakpoint (see media query at the
   bottom of this file). */
.mc-mobile-head { display: none; }
/* Avatar + name on their own row so the name always has full width to wrap
   into (was fighting the badge/share button for space on one crowded row);
   badge + share get their own row underneath instead. */
.mc-mobile-head__top { display: flex; align-items: center; gap: 10px; }
.mc-mobile-avatar {
    width: 44px; height: 44px; border-radius: 50%; flex-shrink: 0;
    background: linear-gradient(135deg,#374151,#4B5563);
    overflow: hidden; position: relative;
}
.mc-mobile-avatar__img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: top center; }
.mc-mobile-head__name { flex: 1; min-width: 0; font-family: 'Manrope', sans-serif; font-size: 16px; font-weight: 800; color: #fff; line-height: 1.25; }

.mc-mobile-head__meta { display: flex; align-items: center; gap: 8px; margin-top: 10px; }
.mc-mobile-head__meta :deep(.share-star-btn) { position: relative; top: auto; right: auto; margin-left: auto; flex-shrink: 0; }
.mc-mobile-head__desc { margin-top: 14px; line-height: 1.5; }

/* CTAs side by side (not stacked full-width) — more compact, room for two per
   row. Extra `.mc-mobile-head` qualifier beats .mc-cta-list's own column rule
   on specificity, since that rule is declared later in this file (source-order
   tie-breaks would otherwise let it win over an equal-specificity override).
   Plain-text chrome (no pill) to match the desktop rail's CTA style — see
   .mc-product__performer .mc-cta-link below, same treatment applied here. */
.mc-mobile-head .mc-cta-list.mc-mobile-head__ctas { flex-direction: row; flex-wrap: wrap; margin-top: 14px; gap: 20px; }
.mc-mobile-head .mc-cta-link {
    height: auto; padding: 0; border-radius: 0;
    background: none; border: none;
    font-size: 14px;
}
.mc-mobile-head .mc-cta-link:hover { background: none; text-decoration: underline; }

.mc-mobile-stats {
    margin-top: 16px; padding: 12px 14px; border-radius: 12px;
    background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.10);
}
.mc-mobile-stats__row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; font-size: 12px; }
.mc-mobile-stats__cast, .mc-mobile-stats__balance { color: #eaeaea; }
.mc-mobile-stats__cast strong, .mc-mobile-stats__balance strong { color: #fff; }
.mc-mobile-stats__balance { display: flex; align-items: center; gap: 5px; }
.mc-mobile-stats__cta { width: 100%; margin-top: 10px; }

/* Header — product title/option count on the left, star balance + "Get more" +
   share button on the right, all aligned in one row. ShareStarProductButton is
   position:absolute by default (for cards with no header row); override it back
   into normal flow here so it sits inline with the other two chips. */
.mc-product__head { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 18px; }
.mc-product__head-left { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
.mc-product__head-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.mc-product__head-right :deep(.share-star-btn) { position: relative; top: auto; right: auto; }

/* Balance pill — one dark capsule holding the star count, with the "Get more"
   CTA nested inside as its own smaller green pill. */
.mc-balance {
    display: inline-flex; align-items: center; gap: 8px;
    height: 40px; padding: 0 6px 0 14px; border-radius: 9999px;
    background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.14);
    white-space: nowrap;
}
.mc-balance__num { font-family: 'Manrope', sans-serif; font-size: 15px; font-weight: 800; color: #fff; }
.mc-balance__lbl { font-size: 13px; color: rgba(234,234,234,0.75); }
.mc-balance__cta {
    height: 32px; padding: 0 16px; border-radius: 9999px;
    background: rgba(52,211,153,0.16); border: 1px solid rgba(52,211,153,0.45); color: #34D399;
    font-size: 13px; font-weight: 700; cursor: pointer;
    transition: background 0.15s;
}
.mc-balance__cta:hover { background: rgba(52,211,153,0.28); }

.mc-closed-chip {
    font-size: 10px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase;
    padding: 3px 9px; border-radius: 9999px;
    background: rgba(255,255,255,0.10); border: 1px solid rgba(255,255,255,0.20); color: #eaeaea;
}
.mc-badge {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 10px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase;
    padding: 3px 9px; border-radius: 9999px;
}
.mc-badge--time { background: rgba(255,255,255,0.10); border: 1px solid rgba(255,255,255,0.20); color: #eaeaea; }
.mc-badge--time.mc-badge--urgent { background: rgba(251,113,133,0.16); border-color: rgba(251,113,133,0.45); color: #FB7185; }
.mc-badge--open { background: rgba(52,211,153,0.16); border: 1px solid rgba(52,211,153,0.45); color: #34D399; }

/* align-items: flex-start (not the flex default, stretch) — otherwise the
   options column stretches to match the performer rail's own height, and
   since the rail's height varies a lot by product (description/CTAs present
   or not), each option's vote-wrap (margin-top: auto, see below) absorbed a
   different amount of leftover space per product, landing the vote CTAs at
   a different height depending on an unrelated column's content. */
.mc-product__layout { display: flex; gap: 24px; align-items: flex-start; }

/* Performer rail — a tall photo up top (aligned with the option media below),
   then name/description/badges/stats/CTAs stacked tightly underneath, in that
   order (compact — no icon on the name, no auto-pushed footer). */
.mc-product__performer { flex: 0 0 320px; display: flex; flex-direction: column; }
.mc-product__photo {
    position: relative; aspect-ratio: 3 / 4;
    border-radius: 12px; overflow: hidden;
    background: linear-gradient(135deg,#1f2937,#111827);
}
.mc-product__photo-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: top center; }
.mc-product__performer-name {
    margin-top: 12px;
    font-family: 'Manrope', sans-serif; font-size: 16px; font-weight: 800; color: #fff;
}
.mc-product__desc { margin-top: 6px; line-height: 1.5; }

/* Divider separates the description from the badges/stats block below it. */
.mc-product__rail-foot { margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(255,255,255,0.10); }
.mc-product__badges { display: flex; flex-wrap: wrap; gap: 6px; }
.mc-product__stats { margin-top: 8px; }

/* CTAs to the linked casting/audition — a product can have both — come last,
   below the badges/stats, side by side rather than stacked. */
.mc-cta-list { display: flex; flex-direction: row; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 14px; }
.mc-cta-link {
    display: inline-flex; align-items: center; gap: 5px;
    height: 32px; padding: 0 14px; border-radius: 9999px;
    background: rgba(52,211,153,0.14); border: 1px solid rgba(52,211,153,0.45); color: #34D399;
    font-size: 12px; font-weight: 700; text-decoration: none;
    transition: background 0.15s;
}
.mc-cta-link:hover { background: rgba(52,211,153,0.24); }

/* Plain text links (no pill chrome) — mobile's version (.mc-mobile-head
   .mc-cta-link above) mirrors this same treatment for visual consistency. */
.mc-product__performer .mc-cta-list { gap: 20px; }
.mc-product__performer .mc-cta-link {
    height: auto; padding: 0; border-radius: 0;
    background: none; border: none;
    font-size: 14px;
}
.mc-product__performer .mc-cta-link:hover { background: none; text-decoration: underline; }

.mc-product__title { font-family: 'Manrope', sans-serif; font-size: 16px; font-weight: 800; letter-spacing: -0.01em; color: #fff; }

/* position:relative anchors .mc-options-frame to THIS column's edges — without
   it the frame falls back to .mc-product's own positioned ancestor and spans
   the full card width (performer rail included), landing the fade/arrows at
   the card's outer edges instead of the options row's. */
.mc-product__content { flex: 1; min-width: 0; display: flex; flex-direction: column; position: relative; }
/* Sibling of .mc-options-row (not inside it) so its overflow-x: auto can't
   clip the burst's radiating particles. CelebrationBurst centers itself
   (left:50%) and anchors 64px from this box's top. */
.mc-burst-anchor { position: absolute; inset: 0; pointer-events: none; z-index: 3; }

/* Options — tall vertical media, title/tally/vote controls below each.
   Desktop always shows exactly 3 tiles' worth of width; a 4th+ option never
   shrinks the rest to fit — it scrolls in horizontally instead (same swipe/fade
   pattern as the mobile breakpoint below, just 3-wide instead of 1-wide).
   flex: 0 0 <basis> forces both grow AND shrink to 0, so tile size is fixed
   regardless of option count. Under 3 options, the row left-aligns rather
   than stretching tiles to fill the leftover space or centering them with
   empty space on both sides. */
.mc-options-row {
    display: flex; gap: 24px; flex: 1; justify-content: flex-start;
    overflow-x: auto; scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch; scrollbar-width: none;
    /* Reaching the last/first option no longer advances to the next/previous
       product — contain stops a swipe past this row's edge from chaining
       into the outer Member's Choice product track (Index.vue's
       .mc-carousel__track); it just stops dead at the edge instead. */
    overscroll-behavior-x: contain;
}
.mc-options-row::-webkit-scrollbar { display: none; }
.mc-options-row { cursor: grab; }
.mc-options-row--dragging {
    cursor: grabbing;
    scroll-snap-type: none;
    user-select: none;
}
.mc-option {
    /* The extra 40px reserved here (beyond the 2 gaps) isn't for spacing —
       it deliberately shrinks all 3 columns slightly so a consistent sliver
       of a 4th option always pokes past the frame's edge when there is one.
       Without it, the "peek" was pure accident: whatever pixels were left
       over after fitting 3 columns capped at 200px. A wide container (the
       main shop page) happened to leave enough leftover to peek; a narrower
       one (the "see all" modal) landed almost exactly on 3 columns with ~0px
       left, so the peek silently vanished there — this guarantees it shows
       in both regardless of container width. */
    flex: 0 0 calc((100% - 48px - 40px) / 3); min-width: 0; max-width: 200px;
    scroll-snap-align: start;
    display: flex; flex-direction: column;
}
/* Real border (not outline) — outline draws outside the box, and since
   .mc-options-row is a scroll container that clips via its implicit
   overflow-y, an outline there gets cropped instead of showing a clean ring.
   A border is part of the box itself (box-sizing: border-box below keeps
   aspect-ratio/height consistent with unbordered options), so it can't be
   clipped by the parent the way outline was. */
.mc-option--leading .mc-option__media { border-color: rgba(52,211,153,0.85); }
.mc-option--winner .mc-option__media { border-color: rgba(252,211,77,0.9); box-shadow: 0 0 24px rgba(252,211,77,0.12); }

.mc-option__media {
    position: relative; aspect-ratio: 4 / 5;
    border-radius: 12px; overflow: hidden; box-sizing: border-box;
    border: 2px solid transparent;
    /* Without this, the gradient below paints straight through the
       transparent border area (default background-clip: border-box),
       reading as a faint border on every card instead of only leading/winner.
       Dark slate (not the brand green) — matches .mc-product__photo's
       placeholder below, and reads as a neutral loading/empty state rather
       than a distinct, attention-grabbing color while media loads. */
    background: linear-gradient(135deg,#1f2937,#111827) padding-box;
}
.mc-option__asset { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; cursor: pointer; }
.mc-play-overlay {
    position: absolute; inset: 0; z-index: 1;
    display: flex; align-items: center; justify-content: center;
    pointer-events: none;
    color: #fff;
}
.mc-play-overlay svg {
    width: 44px; height: 44px; padding: 12px; border-radius: 50%;
    background: rgba(0,0,0,0.45);
}
.mc-ribbon {
    position: absolute; top: 8px; left: 8px;
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 10px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase;
    padding: 4px 10px; border-radius: 9999px;
    background: rgba(252,211,77,0.95); color: #1a1502;
}
.mc-leading {
    position: absolute; top: 8px; left: 8px;
    font-size: 10px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase;
    padding: 3px 9px; border-radius: 9999px;
    /* Nearly-opaque dark backdrop (like .mc-ribbon/.share-star-btn) so the badge
       stays legible over light photo backgrounds, not just dark ones. */
    background: rgba(4,20,14,0.82); backdrop-filter: blur(4px);
    border: 1px solid rgba(52,211,153,0.55); color: #34D399;
}

.mc-option__body { padding-top: 12px; display: flex; flex-direction: column; flex: 1; min-width: 0; }
/* Fixed height reserved regardless of whether this option has a description —
   otherwise an option without one sits shorter than its siblings that have
   one, and that difference pushes the tally/bar/vote CTA below it up or down
   to compensate, so voting CTAs no longer land at the same height across
   options/products. */
.mc-option__text { min-height: 42px; }
.mc-option__title { font-size: 13px; font-weight: 700; }
.mc-option__desc { margin-top: 4px; }
/* Stars/voters on the left, percentage pinned to the right — same row. */
.mc-option__tally { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin: 8px 0 6px; }
.mc-option__tally-left { display: flex; align-items: center; gap: 5px; min-width: 0; }
.mc-option__count { font-family: 'Manrope', sans-serif; font-weight: 800; font-variant-numeric: tabular-nums; font-size: 14px; }
.mc-option__pct { font-family: 'Manrope', sans-serif; font-weight: 800; font-variant-numeric: tabular-nums; font-size: 14px; color: #fff; flex-shrink: 0; }

/* Bar sits directly below the tally row — no "X% of votes" text underneath. */
.mc-bar { height: 5px; background: rgba(255,255,255,0.10); border-radius: 9999px; overflow: hidden; margin-bottom: 14px; }
.mc-bar__fill { height: 100%; background: #34D399; border-radius: 9999px; transition: width 0.3s ease; }

/* Vote controls — CelebrationBurst anchors to this wrapper */
/* Fixed height so single-star and free-amount products both start as a single
   collapsed "Vote" button and reserve the same vertical space — the
   free-amount picker only grows taller once expanded (see .mc-vote-expanded),
   not in this baseline collapsed state. */
/* Top-anchored (not bottom-anchored) — with justify-content: flex-end /
   margin-top: auto, expanding the free-amount picker taller pushed its TOP
   edge further up (the bottom stayed pinned, so the only place for the extra
   height to go was upward). flex-start keeps the button/picker's top fixed
   right after the bar and lets it grow downward instead. */
.mc-vote-wrap {
    position: relative;
    min-height: 78px; display: flex; flex-direction: column; justify-content: flex-start;
}

/* Preset/amount picker, revealed in place of the "Vote" button — bordered so
   it reads as an expanded control rather than just more buttons appearing. */
.mc-vote-expanded {
    display: flex; flex-direction: column; gap: 8px;
    padding: 10px; border-radius: 10px;
    border: 1px solid rgba(52,211,153,0.35); background: rgba(52,211,153,0.06);
}
.mc-vote-cancel {
    background: none; border: none; color: rgba(234,234,234,0.6);
    font-size: 12px; text-align: center; cursor: pointer; padding: 2px 0 0;
}
.mc-vote-cancel:hover { color: #fff; text-decoration: underline; }

.mc-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 5px;
    height: 36px; padding: 0 12px; border-radius: 9px;
    background: rgba(52,211,153,0.14); border: 1px solid rgba(52,211,153,0.45); color: #34D399;
    font-size: 12px; font-weight: 700; cursor: pointer;
    transition: background 0.15s, transform 0.08s;
}
.mc-btn:hover:not(:disabled) { background: rgba(52,211,153,0.24); }
.mc-btn:active:not(:disabled) { transform: scale(0.96); }
.mc-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.mc-btn--full { width: 100%; }
.mc-btn--preset { flex: 1; font-size: 11px; padding: 0 4px; }
.mc-btn--submit { padding: 0 12px; flex-shrink: 0; }

.mc-pick-row { display: flex; gap: 6px; }
.mc-amount-row { display: flex; gap: 6px; }
.mc-amount-field {
    position: relative; flex: 1;
    display: flex; align-items: center;
}
.mc-amount-field__icon { position: absolute; left: 10px; pointer-events: none; }
.mc-amount-input {
    width: 100%; height: 36px; padding: 0 8px 0 28px;
    background: rgba(0,0,0,0.35); border: 1px solid rgba(255,255,255,0.15); border-radius: 9px;
    color: #fff; font-size: 12px;
}
.mc-amount-input::placeholder { color: rgba(234,234,234,0.5); }
.mc-amount-input:focus { outline: none; border-color: rgba(52,211,153,0.55); }
.mc-amount-input::-webkit-outer-spin-button,
.mc-amount-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.mc-amount-input[type='number'] { -moz-appearance: textfield; }

/* Edge-fade hint frame — sits on top of the currently-visible tile(s). Height
   comes from the inline `frameHeight` style (measured off the first tile's
   media via ResizeObserver, see script), starting at 0 until the observer's
   first report. NOT aspect-ratio-derived from the frame's own width: the frame
   spans the FULL row width (all 3 desktop columns), so an aspect-ratio there
   sized off the row's width instead of a single tile's — 3x too tall, and it
   spilled the fade down over the sections below the card. isAtStart/isAtEnd
   (computed generically off scrollLeft) hide whichever fade has nothing left
   to scroll to; with <=3 options both edges report "at start" and "at end"
   immediately so both fades stay invisible. */
.mc-options-frame {
    position: absolute; top: 0; left: 0; right: 0;
    z-index: 2; pointer-events: none; overflow: hidden;
}
.mc-options-fade {
    display: flex; align-items: center;
    position: absolute; top: 0; bottom: 0; width: 44px;
    opacity: 1; transition: opacity 0.2s ease;
}
.mc-options-fade--left { left: 0; justify-content: flex-start; padding-left: 6px; border-radius: 12px 0 0 12px; background: linear-gradient(to right, rgba(18,18,24,0.85), transparent); }
.mc-options-fade--right { right: 0; justify-content: flex-end; padding-right: 6px; border-radius: 0 12px 12px 0; background: linear-gradient(to left, rgba(18,18,24,0.85), transparent); }
.mc-options-frame--at-start .mc-options-fade--left { opacity: 0; }
.mc-options-frame--at-end .mc-options-fade--right { opacity: 0; }

@media (max-width: 640px) {
    /* Swap the desktop header + side-by-side performer rail for the compact
       mobile header defined above — same underlying data, rearranged instead
       of duplicated across breakpoints via a second markup path. */
    .mc-mobile-head { display: block; margin-bottom: 20px; }
    .mc-product__head { display: none; }
    .mc-product__performer { display: none; }

    /* align-items: flex-start (set above for desktop, to stop the rail's
       height from stretching the options column) controls the CROSS axis —
       which flips to horizontal once this becomes a column layout. Left as
       flex-start here, it stopped children from stretching to the card's
       full width, so the options row (and its media) shrank to fit its own
       unconstrained content instead of filling the card. Restore stretch. */
    .mc-product__layout { flex-direction: column; align-items: stretch; }

    /* Options become a swipeable, snapping horizontal row instead of stacking
       full-width — each option keeps its full content (9:16 media + title +
       progress bar + vote controls), just paged through by swipe instead of an
       endless vertical scroll. One option fills the row exactly (no peek of the
       next) so only one is ever visible at a time, regardless of total option
       count — the base .mc-options-row scroll/fade behavior (shared with
       desktop's 3-wide view) already handles paging through the rest.
       max-width: none overrides the desktop 200px cap (line ~508) — without
       it, an option stayed capped at 200px even at flex-basis 100%, leaving
       leftover row width where neighboring options peeked in on both sides. */
    .mc-option { flex: 0 0 100%; max-width: none; }

    /* .mc-options-row--scrollable (real overflow) only gets added at 4+
       options — that threshold is a desktop-3-wide assumption. On mobile
       each option is already 100% width, so a 2nd option alone overflows;
       without this, the still-centered row split its overflow evenly on
       both sides, showing cropped slivers of the neighboring options
       instead of option 1 sitting flush at the start. */
    .mc-options-row { justify-content: flex-start; }
}

</style>
